import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { DashboardLayoutComponent } from '@syncfusion/ej2-react-layouts/src/dashboard-layout/dashboardlayout.component';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BillingGroupRecordDashboardElementDefinition,
  BillingGroupRecordDashboardElementType,
} from 'common/models/Dashboard/EditLayout';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import {
  BillingGroupScreenLayoutSettings,
  DefaultBillingGroupRecordDashboardElements,
} from 'util/Definitions/ScreenLayouts/BillingGroup';
import PanelWrapper from 'components/Dashboards/PanelWrapper';
import PreviewLoaderComponent from 'components/Previews/PreviewLoader';
import SingleFieldElement from '../SingleFieldElement';
import { BillingGroup } from '../../../common/models/BillingGroup';
import MultiLineTextElement from '../MultiLineTextElement';

interface Props {
  billingGroup: BillingGroup | null;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const { unitWidth, unitHeight, gapX, gapY, columns, mediaQueryMaxWidth } =
  BillingGroupScreenLayoutSettings;
const cellSpacing = [gapX, gapY];

export default function ViewRecordScreenLayout({ billingGroup, loadingHandler }: Props) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [screenResizedControl, setScreenResizedControl] = useState(false);
  const [fetchingSavedState, setFetchingSavedState] = useState(false);
  const [fetchedSavedState, setFetchedSavedState] = useState(false);
  const layoutRef = useRef<DashboardLayoutComponent | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const windowResizeHandler = () => setScreenResizedControl((prev) => !prev);
    window.addEventListener('resize', windowResizeHandler);
    return () => window.removeEventListener('resize', windowResizeHandler);
  }, []);

  useEffect(() => {
    loadingHandler && loadingHandler(fetchingSavedState);
  }, [loadingHandler, fetchingSavedState]);

  useEffect(() => {
    const getSavedState = async () => {
      setFetchingSavedState(true);
      setFetchingSavedState(false);
      setFetchedSavedState(true);
    };
    getSavedState();
  }, []);

  const elements = useMemo<BillingGroupRecordDashboardElementDefinition[]>(() => {
    return DefaultBillingGroupRecordDashboardElements;
  }, []);

  const openPreviewHandler = useCallback((type: PreviewEntityType, recordId: number) => {
    setPreviewType(type);
    setPreviewRecordId(recordId);
    setIsPreviewOpen(true);
  }, []);

  const closePreviewHandler = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  const renderElement = useCallback(
    (id: string, type: BillingGroupRecordDashboardElementType) => {
      if (billingGroup) {
        switch (type) {
          case 'Divider':
            return (
              <Box pt="20px">
                <Divider component="div" />
              </Box>
            );
          case 'Spacer':
            return <></>;
          case 'BillingGroupId':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="ID"
                fieldValue={billingGroup.id.toString()}
              />
            );
          case 'BillingGroupName':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Name"
                fieldValue={billingGroup.name}
              />
            );
          case 'BillingGroupClientName':
            return (
              <SingleFieldElement
                fieldTitle="Client"
                fieldValue={billingGroup.clientName ?? ''}
                format="internal-link"
                href={`/clients/${billingGroup.clientID}`}
                previewType="client"
                previewRecordId={billingGroup.clientID}
                openPreviewHandler={openPreviewHandler}
                closePreviewHandler={closePreviewHandler}
              />
            );
          case 'BillingGroupPrimaryContactName':
            return (
              <SingleFieldElement
                fieldTitle="Primary Contact"
                fieldValue={billingGroup.primaryContactName}
                format="internal-link"
                href={`/contacts/${billingGroup.primaryContactID}`}
                previewType="contact"
                previewRecordId={billingGroup.primaryContactID}
                openPreviewHandler={openPreviewHandler}
                closePreviewHandler={closePreviewHandler}
              />
            );

          case 'BillingGroupAddress1':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Address 1"
                fieldValue={billingGroup.address1}
              />
            );
          case 'BillingGroupAddress2':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Address 2"
                fieldValue={billingGroup.address2}
              />
            );
          case 'BillingGroupAddress3':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Address 3"
                fieldValue={billingGroup.address3}
              />
            );
          case 'BillingGroupSuburb':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Suburb"
                fieldValue={billingGroup.suburb}
              />
            );
          case 'BillingGroupState':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="State"
                fieldValue={billingGroup.state}
              />
            );
          case 'BillingGroupPostcode':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Postcode"
                fieldValue={billingGroup.postcode}
              />
            );
          case 'BillingGroupCountry':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Country"
                fieldValue={billingGroup.countryName}
              />
            );

          case 'BillingGroupDescription':
            return <MultiLineTextElement title="Summary" content={billingGroup.description} />;
          default:
            return <div>{id}</div>;
        }
      }
    },
    [billingGroup, openPreviewHandler, closePreviewHandler]
  );

  const layoutResizeStopHandler = useCallback(() => {
    setScreenResizedControl((prev) => !prev);
  }, []);

  useEffect(() => {
    const api = layoutRef.current;
    if (api && fetchedSavedState) {
      const refreshTimeout = setTimeout(() => {
        api.refresh();
        api.refresh();
      }, 250);
      return () => clearTimeout(refreshTimeout);
    }
  }, [fetchedSavedState, screenResizedControl]);

  const renderLayout = useCallback(() => {
    if (!fetchedSavedState) return <></>;

    const mediaQuery = mediaQueryMaxWidth ? `max-width: ${mediaQueryMaxWidth}` : undefined;
    return (
      <DashboardLayoutComponent
        cellSpacing={cellSpacing}
        columns={columns}
        cellAspectRatio={30 / 2}
        resizeStop={layoutResizeStopHandler}
        allowDragging={false}
        allowResizing={false}
        ref={(l) => (layoutRef.current = l)}
        mediaQuery={mediaQuery}
      >
        {elements.map((e) => (
          <PanelWrapper
            key={e.id}
            id={e.id}
            col={e.col}
            row={e.row}
            sizeX={e.sizeX}
            sizeY={e.sizeY}
            minSizeX={e.minSizeX}
            minSizeY={e.minSizeY}
            maxSizeX={e.maxSizeX}
            maxSizeY={e.maxSizeY}
            resizeControl={screenResizedControl}
            resizeIconColor={theme.palette.text.disabled}
            gapX={gapX}
            gapY={gapY}
            unitWidth={unitWidth}
            unitHeight={unitHeight}
          >
            {renderElement(e.id, e.type)}
          </PanelWrapper>
        ))}
      </DashboardLayoutComponent>
    );
  }, [
    elements,
    fetchedSavedState,
    theme.palette.text.disabled,
    screenResizedControl,
    layoutResizeStopHandler,
    renderElement,
  ]);

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        kioskToken={null}
      />
      <Box p="10px" height="100%">
        <div className="control-section">{renderLayout()}</div>
      </Box>
    </>
  );
}
